import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  Users, 
  FolderOpen, 
  BarChart3, 
  Plus,
  FileText,
  Home,
  UserCheck,
  X
} from 'lucide-react';

const Sidebar = ({ userType, isMobileMenuOpen, closeMobileMenu }) => {
  const adminNavItems = [
    { to: '/admin/clients', icon: Users, label: 'Clients', end: false },
    { to: '/admin/enqueteurs', icon: UserCheck, label: 'Enquêteurs', end: false },
    { to: '/admin/dossiers', icon: FolderOpen, label: 'Dossiers', end: false },
    { to: '/admin/dossiers/nouveau', icon: Plus, label: 'Nouveau Dossier', end: true },
    { to: '/admin/statistiques', icon: BarChart3, label: 'Statistiques', end: true },
  ];

  const enqueteurNavItems = [
    { to: '/enqueteur', icon: Home, label: 'Tableau de bord', end: true },
    { to: '/enqueteur/dossiers', icon: FileText, label: 'Mes Dossiers', end: false },
  ];

  const navItems = userType === 'admin' ? adminNavItems : enqueteurNavItems;

  // Fermer le menu mobile quand on clique en dehors (ESC)
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen, closeMobileMenu]);

  // Empêcher le scroll du body quand le menu mobile est ouvert
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Overlay mobile */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div className={`
        bg-white w-64 min-h-screen shadow-lg fixed lg:relative z-50
        transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Header avec bouton fermer (mobile uniquement) */}
        <div className="flex items-center justify-between p-3 border-b border-port-gray-200">
          <div>
            <h1 className="text-xl font-bold text-atl-blue-700">
              Provelliance
            </h1>
            <p className="text-sm text-port-gray-500 mt-1">
              {userType === 'admin' ? 'Administration' : 'Enquêteur'}
            </p>
          </div>
          <button
            onClick={closeMobileMenu}
            className="lg:hidden p-2 text-port-gray-400 hover:text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors"
            aria-label="Fermer le menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6">
          <ul className="space-y-2 px-4">
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={closeMobileMenu}
                  className={({ isActive }) =>
                    `flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-atl-blue-100 text-atl-blue-700 border-r-2 border-atl-blue-700'
                        : 'text-port-gray-600 hover:bg-port-gray-100 hover:text-port-gray-900'
                    }`
                  }
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-port-gray-200 bg-white">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-atl-blue-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-atl-blue-700">
                {userType === 'admin' ? 'A' : 'E'}
              </span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-port-gray-900">
                {userType === 'admin' ? 'Admin User' : 'Enquêteur'}
              </p>
              <p className="text-xs text-port-gray-500">
                {userType === 'admin' ? 'admin@provelliance.fr' : 'enqueteur@provelliance.fr'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
